import { Card, Col, Flex, Row, Typography, Select, Spin } from "antd";
import pallete from "../utils/pallete";
import { Icon } from "@iconify/react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { useState } from "react";
import { format, subDays, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../services/dashboard.service";

const formatNumber = (num) => {
  if (num >= 1000) {
    return (
      num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    );
  }
  return num;
};

// eslint-disable-next-line react/prop-types
const StatisticCard = ({ title, value, icon = 'solar:notebook-minimalistic-bold-duotone' }) => {
  return (
    <Card
      bordered={false}
      style={{
        background: '#fff',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        borderRadius: '8px',
      }}
      styles={{ body: { padding: '20px' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 40, height: 40, backgroundColor: '#F0FAFF', borderRadius: 4 }} >
          <Icon
            icon={icon}
            color={pallete.primary.main}
            width={30}
          />
        </div>
        <Typography.Text
          style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: pallete.primary.main,
            marginTop: 18,
            marginBottom: -3
          }}
        >
          {formatNumber(value)}
        </Typography.Text>
        <Typography.Text style={{ fontSize: '16px', fontWeight: 400 }}>{title}</Typography.Text>
      </div>
    </Card>
  ) 
}

// Manually define Indonesian month names for consistency
const monthNames = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

export default function Dashboard(){
  // const breakpoint = Grid.useBreakpoint();
  const [timeRange, setTimeRange] = useState('7d');
  
  // Get current date for the date range calculations
  const currentDate = new Date();
  const formattedCurrentDate = format(currentDate, 'yyyy-MM-dd');
  
  // Calculate start date based on selected time range
  const getStartDate = () => {
    switch(timeRange) {
      case '7d':
        return format(subDays(currentDate, 6), 'yyyy-MM-dd'); // 7 days including today
      case '1m':
        return format(subDays(currentDate, 29), 'yyyy-MM-dd'); // 30 days including today
      case '1y':
        return format(subDays(currentDate, 364), 'yyyy-MM-dd'); // 365 days including today
      default:
        return format(subDays(currentDate, 6), 'yyyy-MM-dd');
    }
  };

  // Fetch summary data
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardService.fetchSummaryData(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Fetch trend data
  const { data: trendData, isLoading: isTrendLoading } = useQuery({
    queryKey: ['dashboard-trend', timeRange],
    queryFn: () => dashboardService.fetchTrendData(getStartDate(), formattedCurrentDate),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Fetch regional data
  const { data: regionalData, isLoading: isRegionalLoading } = useQuery({
    queryKey: ['dashboard-regional'],
    queryFn: () => dashboardService.fetchRegionalData(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Format regional data for pie chart
  const formatRegionalData = () => {
    if (!regionalData || !regionalData.data || !regionalData.data.regions) {
      return [];
    }

    return regionalData.data.regions.map(region => ({
      id: region.name.toLowerCase(),
      label: region.name === 'WEST' ? 'Barat' : 'Timur',
      value: region.total
    }));
  };

  const pieChartData = formatRegionalData();

  // Format trend data for line chart
  const formatTrendData = () => {
    if (!trendData || !trendData.data || !trendData.data.days) {
      return [{ id: 'data', data: [] }];
    }

    const days = trendData.data.days;
    
    // For yearly view, aggregate by month
    if (timeRange === '1y') {
      // Create a properly ordered array of months
      const last12Months = [];
      
      // Go back 11 months from the current month (total 12 months including current)
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(currentDate);
        monthDate.setMonth(currentDate.getMonth() - i);
        
        const month = monthNames[monthDate.getMonth()];
        const year = monthDate.getFullYear();
        
        last12Months.push({
          monthIndex: 11 - i, // For ordering (0 is oldest, 11 is newest)
          month,
          year,
          label: `${month}`,
          date: new Date(monthDate),
          total: 0
        });
      }
      
      // Aggregate daily data into the correct months
      days.forEach(day => {
        const date = parseISO(day.date);
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        
        // Find the matching month in our array
        const monthData = last12Months.find(m => 
          m.date.getMonth() === monthIndex && m.date.getFullYear() === year
        );
        
        if (monthData) {
          monthData.total += day.total;
        }
      });
      
      // Map to chart format
      const formattedData = last12Months.map(monthData => ({
        x: monthData.label,
        y: monthData.total,
        rawDate: format(monthData.date, 'yyyy-MM-dd'),
        monthIndex: monthData.monthIndex // Keep this for ordering if needed
      }));
      
      return [{ id: 'visits', data: formattedData }];
    }
    
    // For 7d and 1m views, use daily data
    return [{
      id: 'visits',
      data: days
        .map(day => {
          const date = parseISO(day.date);
          return {
            x: format(date, 'dd MMM'),
            y: day.total,
            rawDate: day.date,
            timestamp: date.getTime() // For sorting
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp) // Ensure chronological order
    }];
  };

  // Prepare chart data
  const lineChartData = formatTrendData();
  
  // Calculate min and max values for Y axis
  const getAxisValues = () => {
    if (!lineChartData[0].data.length) {
      return { minValue: 0, maxValue: 10, tickValues: [0, 2, 4, 6, 8, 10] };
    }
    
    const allValues = lineChartData[0].data.map(d => d.y);
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    
    // Ensure we have a reasonable range even with low values
    const min = dataMin === 0 ? 0 : Math.floor(dataMin / 3) * 3;
    const max = dataMax === 0 ? 10 : Math.ceil(dataMax / 3) * 3 + 3;
    
    const tickValues = [];
    const step = Math.ceil((max - min) / 5);
    
    for (let i = min; i <= max; i += step) {
      tickValues.push(i);
    }
    
    return { minValue: min, maxValue: max, tickValues };
  };
  
  const { minValue, maxValue, tickValues } = getAxisValues();

  // Get X axis values for ticks
  const getXAxisTicks = () => {
    if (!lineChartData[0].data.length) return [];
    
    const data = lineChartData[0].data;
    
    if (timeRange === '7d') {
      // For 7d, show all days
      return data.map(d => d.x);
    } else if (timeRange === '1m') {
      // For 1m, show every 5th day approximately
      const step = Math.ceil(data.length / 6);
      return data.filter((_, i) => i % step === 0).map(d => d.x);
    } else {
      // For 1y, show every 2-3 months to avoid crowding
      // But include the first and last month always
      const step = data.length <= 6 ? 1 : data.length <= 9 ? 2 : 3;
      
      // Always include first and last month
      const ticks = [data[0].x];
      
      // Add intermediate months based on step
      for (let i = step; i < data.length - 1; i += step) {
        ticks.push(data[i].x);
      }
      
      // Add the last month (current month)
      if (data.length > 1) {
        ticks.push(data[data.length - 1].x);
      }
      
      return ticks;
    }
  };

  // Get summary data values with fallback to 0
  const getSummaryValue = (key) => {
    if (isSummaryLoading || !summaryData || !summaryData.data) {
      return 0;
    }
    return summaryData.data[key] || 0;
  };

  return (
    <>
      <Row gutter={[20, 20]} >
        <Col xs={24} sm={8}>
          <StatisticCard title={'Sedang Diproses'} value={getSummaryValue('sedangDiproses')} icon="solar:notebook-minimalistic-bold-duotone" />
        </Col>
        <Col xs={12} sm={8}>
          <StatisticCard title={'Data Terpasang'} value={getSummaryValue('dataTerpasang')} icon="solar:file-check-bold-duotone" />
        </Col>
        <Col xs={12} sm={8}>
          <StatisticCard title={'Total Gangguan'} value={getSummaryValue('totalGangguan')} icon="solar:bell-bold-duotone" />
        </Col>
        <Col xs={24} md={10} >
          <Card styles={{ body: { height: 320 } }} >
            <Typography.Title level={5} >
              Jumlah Kustomer Regional
            </Typography.Title>
            {isRegionalLoading ? (
              <div style={{ height: 240, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin />
              </div>
            ) : (
              <ResponsivePie
                data={pieChartData}
                innerRadius={0.5}
                colors={['#066086', '#009AD8']}
                arcLabelsTextColor={'#fff'}
                margin={{ top: 10, right: 10, bottom: 70, left: 10 }}
                enableArcLinkLabels={false}
                tooltip={({ datum }) => {
                  return (
                    <Card styles={{ body: { padding: 8 } }} style={{ borderRadius: 4 }} >
                      <Flex align="center" >
                        <div style={{ width: 15, height: 15, backgroundColor: datum.color, marginRight: 5 }} />
                        <Typography.Text style={{ fontSize: 12, color: pallete.grey[700] }} type="secondary" >
                          {datum.label} :
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: 12, marginLeft: 5 }} >
                          {datum.value}
                        </Typography.Text>
                      </Flex>
                    </Card>
                  )
                }}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'row',
                    justify: false,
                    translateX: -5,
                    translateY: 40,
                    itemsSpacing: 0,
                    itemWidth: 80,
                    itemHeight: 18,
                    itemTextColor: '#999',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 13,
                    symbolShape: 'circle',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemTextColor: '#000'
                        }
                      }
                    ]
                  }
                ]}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={14} >
          <Card styles={{ body: { height: 320 } }} >
            <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Pendaftar Sambungan Baru
              </Typography.Title>
              <Select
                value={timeRange}
                onChange={setTimeRange}
                style={{ width: 120 }}
                options={[
                  { value: '7d', label: '7 Hari' },
                  { value: '1m', label: '1 Bulan' },
                  { value: '1y', label: '1 Tahun' },
                ]}
              />
            </Flex>
            {isTrendLoading ? (
              <div style={{ height: 240, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin />
              </div>
            ) : (
              <ResponsiveLine
                data={lineChartData}
                colors={[pallete.primary.main]}
                margin={{ top: 10, right: 20, bottom: 70, left: 60 }}
                xScale={{
                  type: 'point',
                }}
                yScale={{
                  type: 'linear',
                  min: minValue,
                  max: maxValue,
                  reverse: false,
                  stacked: false
                }}
                curve="monotoneX"
                enableGridX={false}
                gridYValues={tickValues}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  tickValues: getXAxisTicks(),
                  renderTick: ({ x, y, value }) => (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={16}
                        textAnchor="middle"
                        style={{ fill: '#666', fontSize: 12 }}
                      >
                        {value}
                      </text>
                    </g>
                  )
                }}
                axisLeft={{
                  tickValues: tickValues,
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  renderTick: ({ x, y, value }) => (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        textAnchor="end"
                        dominantBaseline="middle"
                        style={{ fill: '#666', fontSize: 12 }}
                      >
                        {value}
                      </text>
                    </g>
                  ),
                  legend: 'Jumlah Pendaftar',
                  legendOffset: -50,
                  legendPosition: 'middle'
                }}
                enableArea={true}
                areaOpacity={0.3}
                areaBaselineValue={minValue}
                areaBlendMode="normal"
                defs={[
                  {
                    id: 'gradientArea',
                    type: 'linearGradient',
                    colors: [
                      { offset: 5, color: pallete.primary.main, opacity: 0.8 },
                      { offset: 95, color: pallete.primary.main, opacity: 0 }
                    ],
                    x1: 0,
                    y1: 0,
                    x2: 0,
                    y2: 1
                  }
                ]}
                fill={[
                  { match: { id: 'visits' }, id: 'gradientArea' }
                ]}
                pointSize={6}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableSlices="x"
                sliceTooltip={({ slice }) => {
                  return (
                    <Card styles={{ body: { padding: 8 } }} style={{ borderRadius: 4, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                      {slice.points.map((point) => (
                        <Flex key={point.id} align="center" style={{ marginBottom: 4 }}>
                          <div style={{ width: 12, height: 12, backgroundColor: point.serieColor, marginRight: 5, borderRadius: '50%' }} />
                          <Typography.Text style={{ fontSize: 12 }}>
                            {timeRange === '1y' 
                              ? `${point.data.x} ${point.data.rawDate.substring(0, 4)}: ${point.data.y}`
                              : `${format(parseISO(point.data.rawDate), 'dd MMM yyyy')}: ${point.data.y}`
                            }
                          </Typography.Text>
                        </Flex>
                      ))}
                    </Card>
                  )
                }}
                useMesh={true}
                enableCrosshair={true}
                crosshairType="bottom"
                theme={{
                  grid: {
                    line: {
                      stroke: '#e0e0e0',
                      strokeWidth: 1,
                      strokeDasharray: '3 3'
                    }
                  },
                  crosshair: {
                    line: {
                      stroke: pallete.primary.main,
                      strokeWidth: 1,
                      strokeOpacity: 0.5
                    }
                  }
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </>
  )
}